import NestedMap from "../../../core/NestedMap.js";
import RuleTemplateInheritingCache from "./RuleTemplateInheritingCache.js";

/**
 * Manage the creation and retrieval of several instances of
 * `RuleTemplateInheritingCache`: one cache is necessary for each
 * combination of {report, scenario, ruleModelClass}.
 *
 * @param {class} [makeCache] - For testing purpose only.
 */
export default class RuleTemplateInheritingCacheManager {
	constructor({
		makeCache = (...rest) => new RuleTemplateInheritingCache(...rest),
	} = {}) {
		this._makeCache = makeCache;
		this._caches = new NestedMap();
	}

	getInheritedRuleTemplateFor({
		row,
		column,
		ruleModelClass,
		scenario,
		forecastReport,
	}) {
		return this._getOrMakeCache({
			forecastReport,
			ruleModelClass,
			scenario,
		}).getInheritedRuleTemplateFor({ row, column });
	}

	addRuleTemplate({ ruleTemplate, scenario, forecastReport }) {
		const cache = this._getCache({
			forecastReport,
			scenario,
			ruleModelClass: ruleTemplate.getRuleModelKind(),
		});

		if (!cache) {
			return;
		}

		cache.addRuleTemplate(ruleTemplate);
	}

	removeRuleTemplate({ ruleTemplate, scenario, forecastReport }) {
		const cache = this._getCache({
			forecastReport,
			scenario,
			ruleModelClass: ruleTemplate.getRuleModelKind(),
		});

		if (!cache) {
			return;
		}

		cache.removeRuleTemplate(ruleTemplate);
	}

	_getOrMakeCache({ forecastReport, ruleModelClass, scenario }) {
		return (
			this._getCache({ forecastReport, ruleModelClass, scenario }) ||
			this._addCache({ forecastReport, ruleModelClass, scenario })
		);
	}

	_getCache({ forecastReport, scenario, ruleModelClass }) {
		return this._caches.get(
			this._makeMapKey({ forecastReport, scenario, ruleModelClass }),
		);
	}

	_addCache({ forecastReport, scenario, ruleModelClass }) {
		const cache = this._makeCacheForContext({
			forecastReport,
			scenario,
			ruleModelClass,
		});

		this._caches.set(
			this._makeMapKey({ forecastReport, scenario, ruleModelClass }),
			cache,
		);

		return cache;
	}

	_makeCacheForContext({ forecastReport, scenario, ruleModelClass }) {
		return this._makeCache({
			forecastReport,
			getOwnRuleTemplateFor: ({ row, column }) =>
				scenario.getOwnRuleTemplateFor({
					rowId: row.getStableId(),
					start: column.getStart(),
					ruleModelClass,
				}),
			expiredRuleTemplates: this._getExpiredRuleTemplates({
				forecastReport,
				scenario,
				ruleModelClass,
			}),
		});
	}

	_makeMapKey({ forecastReport, scenario, ruleModelClass }) {
		return [forecastReport, scenario, ruleModelClass];
	}

	_getExpiredRuleTemplates({ forecastReport, scenario, ruleModelClass }) {
		const oldestReportPeriod = forecastReport.getColumns()[0].getStart();

		const expiredRuleTemplates = scenario
			.getRuleTemplates()
			.filter(
				(ruleTemplate) =>
					ruleTemplate.getRuleModelKind() === ruleModelClass,
			)
			.reduce((ruleTemplates, ruleTemplate) => {
				const isRuleOlder = ruleTemplate
					.getStart()
					.isOlderThan(oldestReportPeriod);

				if (isRuleOlder) {
					ruleTemplates.push(ruleTemplate);
				}

				return ruleTemplates;
			}, []);

		return this._sortRuleTemplatesByStartAsc({ expiredRuleTemplates });
	}

	_sortRuleTemplatesByStartAsc({ expiredRuleTemplates }) {
		return expiredRuleTemplates.sort((a, b) => {
			const aPeriod = a.getStart();
			const bPeriod = b.getStart();

			if (aPeriod.getYear() === bPeriod.getYear()) {
				return aPeriod.getIndex() - bPeriod.getIndex();
			}

			return aPeriod.getYear() - bPeriod.getYear();
		});
	}
}
